/**
 * Deciding whether a bare inline-code span is a file reference.
 *
 * This is a different question from `markdown-links.ts`, which resolves a
 * destination the author already declared to be a link. Here nothing was
 * declared: inline code mostly holds identifiers, commands and refs
 * (`node.meta`, `origin/main`, `bun run dev`), and a wrong guess turns ordinary
 * prose into a dead link. So the bar is higher than for an explicit link — a
 * span needs an unambiguous path prefix, a file extension, or a `:line` suffix
 * before it counts.
 *
 * Ported from t3code's `resolveInlineCodeFileLinkMeta`. The allowlists below
 * are the part worth copying verbatim rather than re-deriving: they encode
 * which TLDs collide with real file extensions, and that list was arrived at by
 * finding the false positives, not by reasoning about it up front.
 */
import { resolvePathLinkTarget } from "./terminal-links";
import { basenameOfPath } from "./filePath";
import {
  buildFileLinkMetaFromTarget,
  resolveMarkdownFileLinkMeta,
  type MarkdownFileLinkMeta,
} from "./markdown-links";

const WINDOWS_DRIVE_PATH_PATTERN = /^[A-Za-z]:[\\/]/;
const WINDOWS_UNC_PATH_PATTERN = /^\\\\/;
const RELATIVE_PATH_PREFIX_PATTERN = /^(~\/|\.{1,2}\/)/;
const POSITION_SUFFIX_PATTERN = /:\d+(?::\d+)?$/;
/** A span containing whitespace or a backtick is prose or a command, not a path. */
const INLINE_CODE_DISQUALIFIER_PATTERN = /[\s`]/;
const PATH_SEPARATOR_PATTERN = /[\\/]/;
const FILE_EXTENSION_PATTERN = /\.[A-Za-z0-9_-]+$/;
const BARE_EXTENSIONLESS_POSITION_PATTERN = /^[A-Za-z0-9_-]+(?::\d+){1,2}$/;

/** Conventional extensionless filenames, so `Makefile:12` still linkifies. */
const EXTENSIONLESS_FILE_NAMES = new Set([
  "Makefile",
  "makefile",
  "GNUmakefile",
  "Dockerfile",
  "Containerfile",
  "Justfile",
  "justfile",
  "Rakefile",
  "Gemfile",
  "Procfile",
  "Brewfile",
  "Caddyfile",
  "Vagrantfile",
  "Jenkinsfile",
  "Podfile",
  "Fastfile",
  "BUILD",
  "WORKSPACE",
  "LICENSE",
  "LICENCE",
  "COPYING",
  "NOTICE",
  "AUTHORS",
  "CONTRIBUTORS",
  "CHANGELOG",
  "README",
  "CODEOWNERS",
]);

const SINGLE_LABEL_HOSTNAMES = new Set(["localhost"]);
const NUMERIC_DOTTED_PATTERN = /^\d+(?:\.\d+)+$/;

// Allowlists, not full public-suffix detection: treating every dotted first
// segment as a host would swallow real paths like `conf.d/x.conf` or
// `Makefile.in:12`. Extensions that double as filename suffixes (`sh`, `md`,
// `ts`, `rs`, `in`, ...) are deliberately absent from both sets.
const GENERIC_HOSTNAME_TLDS = new Set([
  "com",
  "net",
  "org",
  "io",
  "dev",
  "app",
  "ai",
  "co",
  "edu",
  "gov",
  "mil",
  "info",
  "biz",
  "xyz",
  "me",
  "tv",
  "cc",
  "gg",
  "chat",
  "cloud",
  "site",
  "online",
  "tech",
  "store",
  "link",
]);

// Country codes collide with file extensions (`.pl` Perl, `.pt` PyTorch,
// `.es` ES modules), so they only count as host evidence when the candidate
// lacks a :line suffix — an explicit line reference marks a file and wins.
const COUNTRY_HOSTNAME_TLDS = new Set([
  "uk",
  "de",
  "fr",
  "nl",
  "se",
  "no",
  "fi",
  "dk",
  "pl",
  "ch",
  "at",
  "be",
  "es",
  "it",
  "pt",
  "eu",
  "us",
  "ca",
  "au",
  "nz",
  "jp",
  "kr",
  "cn",
  "br",
  "ru",
  "mx",
  "ie",
  "cz",
  "tr",
  "sg",
  "hk",
]);

/** `127.0.0.1`, `localhost`, `example.com`, `1.2.3` — hosts and versions, not files. */
function looksLikeHostname(segment: string, hasPosition: boolean): boolean {
  if (segment.startsWith(".")) return false;
  const lowered = segment.toLowerCase();
  if (SINGLE_LABEL_HOSTNAMES.has(lowered)) return true;
  if (NUMERIC_DOTTED_PATTERN.test(segment)) return true;
  const labels = lowered.split(".");
  const lastLabel = labels[labels.length - 1];
  if (labels.length < 2 || lastLabel === undefined) return false;
  if (GENERIC_HOSTNAME_TLDS.has(lastLabel)) return true;
  return !hasPosition && COUNTRY_HOSTNAME_TLDS.has(lastLabel);
}

/**
 * Windows drive and UNC paths keep their backslashes; any other backslashes are
 * relative Windows-style paths, which neither the shape checks nor the
 * downstream resolver understand — normalize them to forward slashes.
 */
function normalizeInlineCandidate(trimmed: string): string {
  return WINDOWS_DRIVE_PATH_PATTERN.test(trimmed) || WINDOWS_UNC_PATH_PATTERN.test(trimmed)
    ? trimmed
    : trimmed.replaceAll("\\", "/");
}

function hasExplicitPathShape(candidate: string): boolean {
  return (
    RELATIVE_PATH_PREFIX_PATTERN.test(candidate) ||
    candidate.startsWith("/") ||
    WINDOWS_DRIVE_PATH_PATTERN.test(candidate) ||
    WINDOWS_UNC_PATH_PATTERN.test(candidate)
  );
}

/**
 * For a span with no `./`, `/` or drive prefix, the evidence has to come from
 * the span itself: not a hostname, and either a file extension or a `:line`.
 */
function isLikelyBarePathReference(candidate: string, hasPosition: boolean): boolean {
  const withoutPosition = candidate.replace(POSITION_SUFFIX_PATTERN, "");
  const firstSegment = withoutPosition.split("/")[0] ?? withoutPosition;
  if (looksLikeHostname(firstSegment, hasPosition)) return false;
  return hasPosition || FILE_EXTENSION_PATTERN.test(basenameOfPath(withoutPosition));
}

/**
 * `Makefile:12` — conventional extensionless names fail the generic
 * markdown-link candidate patterns, but the `:line` suffix has already marked
 * the span as a file reference, so resolve it directly.
 */
function resolveExtensionlessPositionReference(
  candidate: string,
  cwd: string,
): MarkdownFileLinkMeta | null {
  if (!BARE_EXTENSIONLESS_POSITION_PATTERN.test(candidate)) return null;
  if (!EXTENSIONLESS_FILE_NAMES.has(candidate.replace(POSITION_SUFFIX_PATTERN, ""))) return null;
  return buildFileLinkMetaFromTarget(resolvePathLinkTarget(candidate, cwd), cwd);
}

export function resolveInlineCodeFileLinkMeta(
  codeText: string,
  cwd?: string,
): MarkdownFileLinkMeta | null {
  const trimmed = codeText.trim();
  if (trimmed.length === 0 || INLINE_CODE_DISQUALIFIER_PATTERN.test(trimmed)) return null;

  const candidate = normalizeInlineCandidate(trimmed);
  const hasPosition = POSITION_SUFFIX_PATTERN.test(candidate);
  if (!hasPosition && !PATH_SEPARATOR_PATTERN.test(candidate)) return null;
  if (!hasExplicitPathShape(candidate) && !isLikelyBarePathReference(candidate, hasPosition)) {
    return null;
  }

  return (
    resolveMarkdownFileLinkMeta(candidate, cwd) ??
    (cwd ? resolveExtensionlessPositionReference(candidate, cwd) : null)
  );
}
